import React from "react";
import { Row, Col, Button, Typography } from "antd";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Lock,
  Eye,
  Database,
  Shield,
  Bell,
  RefreshCw,
  UserCheck,
  ShieldCheck,
  Fingerprint,
} from "lucide-react";
import logoText from "@assets/images/logo/light-logo-text-nobg.png";
import { SpaceBackground } from "@components";

const { Title, Paragraph } = Typography;

const Privacy = () => {
  const history = useHistory();

  const sections = [
    {
      icon: Shield,
      title: "Information Protection",
      content: `At Copilotly, we prioritize the protection of your personal information. We implement industry-standard security measures to prevent unauthorized access, disclosure, or misuse of your data.

We collect only the minimum information necessary to provide you with our services and continuously improve your experience.`,
    },
    {
      icon: Database,
      title: "Information We Collect",
      content: `We collect:
• Account information (name, email, profile details)
• Usage data (how you interact with our services)
• Device information (browser type, IP address, device type)
• Payment information (processed securely through our payment providers)

This information helps us provide and improve our services while ensuring security.`,
    },
    {
      icon: Eye,
      title: "How We Use Your Information",
      content: `We use your information to:
• Provide and maintain our services
• Improve and personalize your experience
• Process your transactions
• Send important updates and notifications
• Analyze usage patterns to enhance our platform
• Protect against fraud and abuse`,
    },
    {
      icon: Lock,
      title: "Data Security",
      content: `Your data security is our priority. We employ:
• Enterprise-grade encryption for data transmission
• Secure data storage systems
• Regular security audits
• Strict access controls
• Continuous monitoring for suspicious activities`,
    },
    {
      icon: UserCheck,
      title: "Your Privacy Rights",
      content: `You have the right to:
• Access your personal information
• Request data correction or deletion
• Opt out of marketing communications
• Export your data
• Understand how your data is processed

Contact us at privacy@copilotly.com to exercise these rights.`,
    },
    {
      icon: Bell,
      title: "Communications",
      content: `We may contact you regarding:
• Service updates and maintenance
• Security alerts
• Account notifications
• Marketing communications (with your consent)

You can opt out of marketing communications at any time.`,
    },
    {
      icon: RefreshCw,
      title: "Policy Updates",
      content: `We may update this policy periodically to reflect changes in our practices or legal requirements. We'll notify you of significant changes through:
• Email notifications
• Website announcements
• In-app notifications

Continuing to use our services after changes indicates acceptance of the updated policy.`,
    },
  ];

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Privacy Policy | Copilotly</title>
        <meta
          name="description"
          content="Learn how Copilotly protects your privacy and handles your personal information. Read our comprehensive privacy policy."
        />
        <link rel="canonical" href="https://app.copilotly.com/privacy" />
      </Helmet>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#0a0612",
          overflowY: "auto",
        }}
      >
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
          <SpaceBackground />
        </div>
        <div
          style={{
            padding: "40px 20px 80px 20px",
            position: "relative",
            minHeight: "100%",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              onClick={() => history.goBack()}
              style={{
                position: "fixed",
                top: 20,
                left: 20,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(167, 139, 250, 0.15)",
                border: "1px solid rgba(167, 139, 250, 0.3)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                zIndex: 1000,
                color: "#ffffff",
                fontWeight: 500,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding: "8px 16px",
                height: "auto",
              }}
            >
              <ChevronLeft size={18} style={{ color: "#a78bfa" }} />
              <span>Back</span>
            </Button>
          </motion.div>

          <Row justify="center">
            <Col xs={24} sm={24} md={20} lg={16} xl={14}>
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  textAlign: "center",
                  marginBottom: "40px",
                  marginTop: "60px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={logoText}
                  alt="Copilotly"
                  style={{
                    width: "200px",
                    marginBottom: "32px",
                    display: "block",
                  }}
                />

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.15)",
                    borderRadius: "20px",
                    padding: "8px 16px",
                    marginBottom: "20px",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(167, 139, 250, 0.2)",
                  }}
                >
                  <Fingerprint size={16} color="#a78bfa" />
                  <span style={{ color: "#c4b5fd", fontSize: "13px", fontWeight: 500 }}>
                    Your Data, Your Rights
                  </span>
                </div>

                <h1
                  style={{
                    color: "#fff",
                    fontSize: "clamp(32px, 5vw, 48px)",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    margin: "0 0 16px 0",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Privacy{" "}
                  <span
                    style={{
                      background: "linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Policy
                  </span>
                </h1>

                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "14px",
                    margin: 0,
                  }}
                >
                  Last updated: December 2024
                </p>
              </motion.div>

              {/* Intro Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{
                  background: "rgba(167, 139, 250, 0.08)",
                  borderRadius: "20px",
                  padding: "28px",
                  marginBottom: "32px",
                  border: "1px solid rgba(167, 139, 250, 0.15)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      background: "rgba(167, 139, 250, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShieldCheck size={20} color="#a78bfa" />
                  </div>
                  <span style={{ color: "#fff", fontSize: "18px", fontWeight: 600 }}>
                    Our Privacy Commitment
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "15px",
                    color: "rgba(255,255,255,0.7)",
                    margin: 0,
                    lineHeight: 1.7,
                  }}
                >
                  Your privacy is critically important to us. At Copilotly, we have a few fundamental principles: we don't ask you for personal information unless we truly need it, we don't share your personal information with anyone except to comply with the law or protect our rights, and we treat your data with the utmost care and respect.
                </p>
              </motion.div>

              {/* Sections */}
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "16px",
                      padding: "24px",
                      marginBottom: "16px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "12px",
                          background: "rgba(167, 139, 250, 0.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={22} color="#a78bfa" />
                      </div>
                      <h3
                        style={{
                          margin: 0,
                          color: "#ffffff",
                          fontSize: "18px",
                          fontWeight: 600,
                        }}
                      >
                        {section.title}
                      </h3>
                    </div>
                    <p
                      style={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "15px",
                        lineHeight: 1.8,
                        whiteSpace: "pre-line",
                        margin: 0,
                        paddingLeft: "58px",
                      }}
                    >
                      {section.content}
                    </p>
                  </motion.div>
                );
              })}

              {/* Contact Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.55 }}
                style={{
                  background: "linear-gradient(135deg, rgba(167, 139, 250, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)",
                  borderRadius: "20px",
                  padding: "32px",
                  marginTop: "32px",
                  marginBottom: "32px",
                  textAlign: "center",
                  border: "1px solid rgba(167, 139, 250, 0.2)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <h4
                  style={{
                    color: "#ffffff",
                    marginBottom: "12px",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Questions about our Privacy Policy?
                </h4>
                <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "20px", fontSize: "15px" }}>
                  Our Privacy Team is here to help clarify any concerns.
                </p>
                <a
                  href="mailto:privacy@copilotly.com"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.2)",
                    border: "1px solid rgba(167, 139, 250, 0.4)",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    color: "#c4b5fd",
                    fontWeight: 500,
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  privacy@copilotly.com
                </a>
              </motion.div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Privacy;
